import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { catchError, Observable, Subject, switchMap, throwError } from 'rxjs';
import { ACCESS_TOKEN_KEY, AuthService } from '@core/services';
import { StoreService } from '@core/services/store';

let _refreshSubject: Subject<any> = new Subject<any>();

function updateHeader(req: HttpRequest<unknown>, storeService: StoreService): HttpRequest<unknown> {
  const accessToken = storeService.getItem(ACCESS_TOKEN_KEY);
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
  });
}

function checkTokenExpiryErr(error: HttpErrorResponse): boolean {
  return !!error.status && error.status === 401 && !!error.error;
}

function ifTokenExpired(authService: AuthService) {
  _refreshSubject.subscribe({
    complete: () => {
      _refreshSubject = new Subject<any>();
    },
  });

  if (_refreshSubject.observers.length === 1) {
    authService.validateToken().subscribe(_refreshSubject);
  }
  return _refreshSubject;
}

/**
 * Functional interceptor to add JWT token to the request headers.
 * Handles token refresh on 401 responses.
 */
export const jwtInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const storeService = inject(StoreService);
  const ngZone = inject(NgZone);

  if (req.url.endsWith('/login') || req.url.endsWith('/logout') || req.url.endsWith('/refresh')) {
    return next(req);
  }

  // Add token but skip refresh-retry loop for the current-user permissions endpoint
  // to avoid an infinite loop (loadPermissions is called inside validateToken's switchMap).
  if (req.url.endsWith('/permissions/user')) {
    return next(updateHeader(req, storeService));
  }

  const authedReq = updateHeader(req, storeService);
  return next(authedReq).pipe(
    catchError((error, caught) => {
      if (error instanceof HttpErrorResponse) {
        if (checkTokenExpiryErr(error)) {
          const refreshed$ = ifTokenExpired(authService).pipe(
            switchMap(() => next(updateHeader(req, storeService)))
          );
          // Run the retried request in the Angular zone to ensure change detection runs correctly
          return new Observable<any>((observer) => {
            const sub = refreshed$.subscribe({
              next: (val) => ngZone.run(() => observer.next(val)),
              error: (err) => ngZone.run(() => observer.error(err)),
              complete: () => ngZone.run(() => observer.complete()),
            });
            return () => sub.unsubscribe();
          });
        } else {
          return throwError(() => error);
        }
      }
      return caught;
    }),
  );
};;;
